import { useEffect, useState } from "react";
import { Pencil, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Pill } from "@/components/Common";
import { Field } from "@/components/ProfileFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageTitle } from "./AdminLayout";
import { Confirm } from "@/components/Common";

const EMPTY = { name: "", email: "", password: "", role: "keeper", can_manage_routes: false, active: true };

function StaffDialog({ open, onOpenChange, onDone, staff }) {
  const [v, setV] = useState(EMPTY);
  useEffect(() => {
    setV(staff ? { ...EMPTY, ...staff, password: "" } : EMPTY);
  }, [staff, open]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...v };
      if (!payload.password) delete payload.password;
      if (staff) {
        await api.put(`/admin/staff/${staff.id}`, payload);
        toast.success("Staf diperbarui.");
      } else {
        await api.post("/admin/staff", payload);
        toast.success("Staf ditambahkan.");
      }
      onDone();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="staff-editor-dialog">
        <DialogHeader><DialogTitle>{staff ? "Edit staf" : "Tambah staf"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Nama"><Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} required data-testid="staff-name-input" /></Field>
          <Field label="Email"><Input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} required data-testid="staff-email-input" /></Field>
          <Field label={staff ? "Password baru (opsional)" : "Password (min 8)"}><Input type="password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} required={!staff} data-testid="staff-password-input" /></Field>
          <Field label="Peran">
            <Select value={v.role} onValueChange={(role) => setV({ ...v, role })}>
              <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="keeper">Keeper</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
            </Select>
          </Field>
          {v.role === "keeper" && <label className="flex items-center gap-2 text-sm"><Switch checked={v.can_manage_routes} onCheckedChange={(c) => setV({ ...v, can_manage_routes: c })} data-testid="staff-manage-routes-switch" /> Boleh buka/tutup jalur</label>}
          {staff && <label className="flex items-center gap-2 text-sm"><Switch checked={v.active} onCheckedChange={(c) => setV({ ...v, active: c })} data-testid="staff-active-switch" /> Akun aktif</label>}
          <Button type="submit" className="w-full" data-testid="staff-editor-submit-button">Simpan</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Staff() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/staff").then((r) => setItems(r.data.items));
  useEffect(() => {
    load();
  }, []);
  const remove = async (staff) => {
    try {
      await api.delete(`/admin/staff/${staff.id}`);
      toast.success("Staf dihapus.");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageTitle title="Staf / Keeper" desc="Kelola akses admin dan keeper untuk operasional event.">
        <Button className="rounded-full" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-staff-button"><UserPlus className="mr-1.5 h-4 w-4" /> Tambah Staf</Button>
      </PageTitle>
      <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-[#1A3323]/10">
        <Table data-testid="staff-table">
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Peran</TableHead><TableHead>Kelola jalur</TableHead><TableHead>Aktif</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                <TableCell className="font-semibold">{s.name}</TableCell>
                <TableCell>{s.email}</TableCell>
                <TableCell><Pill className={s.role === "admin" ? "bg-[#1A3323] text-white" : "bg-[#EDE9DC] text-[#1A3323]"}>{s.role}</Pill></TableCell>
                <TableCell>{s.role === "keeper" ? (s.can_manage_routes ? "Ya" : "Tidak") : "Ya"}</TableCell>
                <TableCell>{s.active ? "Ya" : "Tidak"}</TableCell>
                <TableCell><div className="flex gap-1"><Button size="icon" variant="ghost" onClick={() => { setEditing(s); setOpen(true); }} data-testid={`edit-staff-${s.id}`}><Pencil className="h-4 w-4" /></Button><Confirm testid={`delete-staff-${s.id}`} title="Hapus staf?" desc="Akses akun dan penugasan event staf ini akan dihapus." destructive confirmText="Hapus" onConfirm={() => remove(s)} trigger={<Button size="icon" variant="ghost" className="text-red-700" data-testid={`delete-staff-${s.id}`}><Trash2 className="h-4 w-4" /></Button>} /></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <StaffDialog open={open} onOpenChange={setOpen} onDone={load} staff={editing} />
    </div>
  );
}
